import { useMutation, useQueryClient } from '@tanstack/react-query'
import { BookOpen, ChefHat, Pencil, Printer, ScrollText, Trash2 } from 'lucide-react'
import { useEffect, useImperativeHandle, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router'
import { MEAL_NAMES } from '../../../shared/meals'
import { FormError } from '@/components/FormError'
import { OriginalPhoto } from '@/components/OriginalPhoto'
import { Initial } from '@/components/paper'
import { MadeIt } from '@/components/recipes/MadeIt'
import { Avatar } from '@/components/tree/Avatar'
import { api } from '@/lib/api'
import { postSlides, type Slide } from '@/lib/modernRecipes'
import { photoUrl } from '@/lib/photos'
import { lines, madePhotos, useAllRecipes, type Recipe, type RecipePhoto } from '@/lib/recipes'
import { cn } from '@/lib/utils'

const pill =
  'inline-flex min-h-11 items-center justify-center gap-2 rounded-full px-5 text-sm font-medium transition-colors outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50'

const madeWhen = (at: number | undefined) =>
  at ? new Intl.DateTimeFormat(undefined, { month: 'long', year: 'numeric' }).format(at) : ''

// The Modern recipe: a post. The dish first, then a swipe to its card in the hand it was written in, then the dish
// as others in the family have made it. Who it's from and the family's notes sit above the words, with Cook, the
// card and "I made this" a tap away. Beside it on a computer, below it on a phone. The keeper's own controls (Edit,
// Send to book, Delete) come in from the page, which shares them with the Traditional look.
export function RecipePost({
  recipe,
  keeper,
  waiting,
}: {
  recipe: Recipe
  keeper?: React.ReactNode
  waiting?: React.ReactNode
}) {
  const [search] = useSearchParams()
  const slides = postSlides(recipe)
  const firstCard = slides.findIndex((s) => s.kind === 'card')
  const ingredients = lines(recipe.ingredients)
  const steps = lines(recipe.steps)
  const notes = recipe.notes.trim()
  const made = madePhotos(recipe)
  const from = recipe.from ? { ...recipe.from, portrait: recipe.from.portrait ?? null } : null
  const carousel = useRef<CarouselHandle>(null)

  return (
    <article className="mx-auto flex w-full max-w-6xl flex-col gap-6 pb-10 nav:pt-8 lg:flex-row lg:items-start lg:gap-12 lg:px-6">
      {slides.length > 0 && (
        <div className="min-w-0 lg:sticky lg:top-8 lg:flex-[5_1_0%]">
          <Carousel
            ref={carousel}
            recipe={recipe}
            slides={slides}
            start={search.get('show') === 'card' && firstCard >= 0 ? firstCard : 0}
          />
        </div>
      )}

      <div
        className={cn(
          'flex min-w-0 flex-col gap-6 px-4 lg:px-0',
          slides.length ? 'lg:flex-[4_1_0%]' : 'mx-auto w-full max-w-(--measure)',
        )}
      >
        <header className="flex flex-col gap-3">
          <div className="flex items-center gap-3">
            {from ? (
              <Link
                to={`/person/${from.id}`}
                className="flex min-w-0 items-center gap-2.5 rounded-full outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
              >
                <Avatar person={from} className="size-10 text-sm shadow-none" />
                <span className="min-w-0 leading-tight">
                  <span className="block truncate font-semibold">From {from.name}’s kitchen</span>
                  <span className="block truncate text-sub text-muted-foreground">Added by {recipe.by}</span>
                </span>
              </Link>
            ) : (
              <span className="flex min-w-0 items-center gap-2.5">
                <Initial name={recipe.by} portrait={recipe.byPortrait} index={2} className="size-10 text-sm" />
                <span className="truncate font-semibold">Added by {recipe.by}</span>
              </span>
            )}
          </div>
          <h1 className="text-display text-balance">{recipe.title}</h1>
          {notes && <p className="text-lg leading-relaxed whitespace-pre-wrap">{notes}</p>}
          {(recipe.meals.length > 0 || recipe.book) && (
            <ul aria-label="Where it’s kept" className="flex flex-wrap gap-2">
              {recipe.book && (
                <li>
                  <Link
                    to={`/book/${recipe.book.id}`}
                    viewTransition
                    className={cn(pill, 'min-h-9 bg-secondary px-3.5 hover:bg-accent pointer-coarse:min-h-11')}
                  >
                    <BookOpen className="size-4" aria-hidden /> {recipe.book.title}
                  </Link>
                </li>
              )}
              {recipe.meals.map((meal) => (
                <li key={meal}>
                  <span className="inline-flex min-h-9 items-center rounded-full bg-secondary px-3.5 text-sm font-medium">
                    {MEAL_NAMES[meal].one}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </header>

        {waiting}

        <div className="flex flex-wrap gap-2.5">
          <Link
            to={`/recipe/${recipe.id}/cook`}
            className={cn(pill, 'h-12 flex-1 basis-full bg-primary text-base text-primary-foreground sm:basis-auto')}
          >
            <ChefHat className="size-5" aria-hidden /> Cook
          </Link>
          {firstCard >= 0 && (
            <button
              type="button"
              onClick={() => carousel.current?.show(firstCard)}
              className={cn(pill, 'bg-secondary hover:bg-accent')}
            >
              <ScrollText className="size-4" aria-hidden /> The card
            </button>
          )}
          <MadeIt recipeId={recipe.id} className="bg-secondary hover:bg-accent" />
          <Link to={`/recipe/${recipe.id}/print`} className={cn(pill, 'bg-secondary hover:bg-accent')}>
            <Printer className="size-4" aria-hidden /> Print
          </Link>
          {recipe.mine && (
            <Link to={`/recipe/${recipe.id}/edit`} className={cn(pill, 'bg-secondary hover:bg-accent')}>
              <Pencil className="size-4" aria-hidden /> Edit
            </Link>
          )}
          {keeper}
        </div>

        {ingredients.length > 0 && (
          <section aria-labelledby="post-ingredients" className="flex flex-col gap-2">
            <h2 id="post-ingredients" className="eyebrow">
              Ingredients
            </h2>
            <ul className="flex flex-col divide-y">
              {ingredients.map((line, i) => (
                <li key={i} className="py-2.5 text-lg">
                  {line}
                </li>
              ))}
            </ul>
          </section>
        )}
        {steps.length > 0 && (
          <section aria-labelledby="post-steps" className="flex flex-col gap-3">
            <h2 id="post-steps" className="eyebrow">
              Steps
            </h2>
            <ol className="flex flex-col gap-4">
              {steps.map((line, i) => (
                <li key={i} className="flex gap-4 text-lg leading-relaxed">
                  <span aria-hidden className="w-6 shrink-0 font-semibold text-muted-foreground">
                    {i + 1}
                  </span>
                  <span>{line}</span>
                </li>
              ))}
            </ol>
          </section>
        )}

        <MadeByTheFamily recipe={recipe} photos={made} />
        {from && <MoreFrom recipe={recipe} from={from} />}
      </div>
    </article>
  )
}

type CarouselHandle = { show: (i: number) => void }

// The photos one after another, a swipe apart, each shown whole on the same height so nothing jumps. The first is
// the photo on the recipe's tile, carrying the tile's name so it grows out of the wall when the recipe opens.
function Carousel({
  ref,
  recipe,
  slides,
  start,
}: {
  ref: React.Ref<CarouselHandle>
  recipe: Recipe
  slides: Slide[]
  start: number
}) {
  const strip = useRef<HTMLUListElement>(null)
  const [at, setAt] = useState(start)
  const show = (i: number, smooth = true) => {
    const el = strip.current
    if (!el) return
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches
    el.scrollTo({ left: i * el.clientWidth, behavior: smooth && !reduce ? 'smooth' : 'instant' })
  }
  useImperativeHandle(ref, () => ({ show }))
  useEffect(() => {
    if (start) show(start, false)
    // Only where it opens; later changes come from the person.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const onScroll = () => {
    const el = strip.current
    if (el) setAt(Math.round(el.scrollLeft / Math.max(1, el.clientWidth)))
  }

  return (
    <figure
      className="relative flex flex-col gap-3"
      aria-roledescription="carousel"
      aria-label={`Photos of ${recipe.title}`}
    >
      <ul
        ref={strip}
        onScroll={onScroll}
        className="flex snap-x snap-mandatory [scrollbar-width:none] overflow-x-auto overscroll-x-contain lg:rounded-[1.5rem] [&::-webkit-scrollbar]:hidden"
      >
        {slides.map((s, i) => (
          <li
            key={s.photo.id}
            aria-roledescription="slide"
            aria-label={`${i + 1} of ${slides.length}`}
            className={cn(
              'grid h-[min(78svh,40rem)] w-full shrink-0 snap-center place-items-center',
              s.kind === 'card' ? 'bg-secondary p-6' : 'bg-viewer',
            )}
          >
            <OriginalPhoto
              src={photoUrl(s.photo.id, 'original')}
              fallback={photoUrl(s.photo.ref, 'display')}
              alt={slideWords(s, recipe.title)}
            >
              <button
                type="button"
                className="grid size-full place-items-center outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:ring-inset"
              >
                <img
                  src={photoUrl(s.photo.ref, 'display')}
                  width={s.photo.width}
                  height={s.photo.height}
                  alt={slideWords(s, recipe.title)}
                  loading={i === start ? 'eager' : 'lazy'}
                  decoding="async"
                  style={i === 0 ? { viewTransitionName: `recipe-${recipe.id}` } : undefined}
                  className={cn('max-h-full max-w-full object-contain', s.kind === 'card' && 'rounded-sm shadow-paper')}
                />
              </button>
            </OriginalPhoto>
          </li>
        ))}
      </ul>
      {slides.length > 1 && (
        <>
          <span
            aria-hidden
            className="absolute top-3 right-3 rounded-full bg-black/55 px-2.5 py-0.5 text-xs font-semibold text-white tabular-nums"
          >
            {at + 1}/{slides.length}
          </span>
          <figcaption className="flex items-center justify-center gap-1.5 px-4">
            {slides.map((s, i) => (
              <button
                key={s.photo.id}
                type="button"
                aria-label={`Show ${slideWords(s, recipe.title)}`}
                aria-current={i === at}
                onClick={() => show(i)}
                className="grid size-6 place-items-center rounded-full outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
              >
                <span
                  className={cn(
                    'size-1.5 rounded-full transition-[background-color,scale] duration-300',
                    i === at ? 'scale-125 bg-brand' : 'bg-foreground/25',
                  )}
                />
              </button>
            ))}
          </figcaption>
          {slides[at]?.kind === 'made' && (
            <p className="-mt-1 text-center text-sub text-muted-foreground">As {slides[at]!.photo.by} made it</p>
          )}
        </>
      )}
    </figure>
  )
}

const slideWords = (s: Slide, title: string) =>
  s.kind === 'card'
    ? `The card for ${title}`
    : s.kind === 'made'
      ? `${title} as ${s.photo.by ?? 'someone'} made it`
      : title

// The dish as people in the family have made it, newest first, each with who and when. Whoever added a photo can take
// it off again, and so can the recipe's keeper.
export function MadeByTheFamily({ recipe, photos }: { recipe: Recipe; photos: RecipePhoto[] }) {
  const queryClient = useQueryClient()
  const remove = useMutation({
    mutationFn: (id: string) => api(`/photos/${id}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['recipe', recipe.id] }),
  })
  const newest = [...photos].sort((a, b) => (b.addedAt ?? 0) - (a.addedAt ?? 0))
  return (
    <section
      aria-labelledby="made-by-family"
      className="flex flex-col gap-3 border-t pt-6"
      data-testid="made-by-family"
    >
      <div className="flex items-center justify-between gap-3">
        <h2 id="made-by-family" className="text-lg font-semibold">
          Made by others
        </h2>
      </div>
      {newest.length === 0 ? (
        <p className="text-muted-foreground">
          When you make it, add a photo of yours and it shows here with your name.
        </p>
      ) : (
        <ul className="-mx-4 scroll-row gap-3 px-4 lg:mx-0 lg:px-0">
          {newest.map((p) => (
            <li key={p.id} className="flex w-40 shrink-0 snap-start flex-col gap-2">
              <div className="relative">
                <OriginalPhoto
                  src={photoUrl(p.id, 'original')}
                  fallback={photoUrl(p.ref, 'display')}
                  alt={`${recipe.title} as ${p.by} made it`}
                >
                  <button
                    type="button"
                    className="block aspect-square w-full overflow-hidden rounded-2xl bg-muted outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
                  >
                    <img
                      src={photoUrl(p.ref, 'thumb')}
                      alt={`${recipe.title} as ${p.by} made it`}
                      loading="lazy"
                      decoding="async"
                      className="size-full object-cover"
                    />
                  </button>
                </OriginalPhoto>
                {(p.mine || recipe.mine) && (
                  <button
                    type="button"
                    aria-label={p.mine ? 'Take your photo off' : `Take ${p.by}’s photo off`}
                    disabled={remove.isPending}
                    onClick={() => remove.mutate(p.id)}
                    className="absolute top-1.5 right-1.5 grid size-9 place-items-center rounded-full bg-background/90 shadow-sm outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
                  >
                    <Trash2 className="size-4" aria-hidden />
                  </button>
                )}
              </div>
              <p className="flex items-center gap-2 text-sub leading-tight">
                <Initial
                  name={p.by ?? ''}
                  portrait={p.byPortrait ?? null}
                  index={1}
                  className="size-6 shrink-0 text-[0.625rem]"
                />
                <span className="min-w-0">
                  <span className="block truncate font-medium">{p.mine ? 'You made it' : `${p.by} made it`}</span>
                  <span className="block truncate text-muted-foreground">{madeWhen(p.addedAt)}</span>
                </span>
              </p>
            </li>
          ))}
        </ul>
      )}
      <FormError error={remove.error} />
    </section>
  )
}

// The other recipes from the same kitchen, as small tiles to keep going.
function MoreFrom({ recipe, from }: { recipe: Recipe; from: { id: string; name: string } }) {
  const all = useAllRecipes(recipe.familyId, false)
  const more = (all.data?.recipes ?? []).filter((x) => x.from?.id === from.id && x.id !== recipe.id).slice(0, 6)
  if (!more.length) return null
  return (
    <section aria-labelledby="more-from" className="flex flex-col gap-3 border-t pt-6">
      <h2 id="more-from" className="text-lg font-semibold">
        More from {from.name}’s kitchen
      </h2>
      <ul className="grid grid-cols-3 gap-2.5">
        {more.map((x) => (
          <li key={x.id}>
            <Link
              to={`/recipe/${x.id}`}
              viewTransition
              className="flex flex-col gap-1.5 rounded-xl outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
            >
              <span className="block aspect-square overflow-hidden rounded-xl bg-secondary">
                {x.tile ? (
                  <img src={photoUrl(x.tile.ref, 'thumb')} alt="" loading="lazy" className="size-full object-cover" />
                ) : (
                  <span className="grid size-full place-items-center p-2 text-center text-sm font-semibold">
                    {x.title}
                  </span>
                )}
              </span>
              <span className="line-clamp-2 text-sub font-medium">{x.title}</span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  )
}
